// ═══════════════════════════════════════════════════════════════════
//  usePeerCursors — Supabase Realtime presence → PeerCursor[].
// ═══════════════════════════════════════════════════════════════════
//
//  Wraps a presence-only channel (separate from the existing
//  useRealtimeSync's full-state channel so cursor traffic doesn't
//  interfere with snapshot sync). Subscribes to `cursors:<workflowId>`,
//  tracks the local user's cursor on a throttle, and aggregates the
//  presence state into the `PeerCursor[]` shape the Minimap consumes.
//
//  Local user is excluded from the returned list so the Minimap doesn't
//  render a cursor over its own position.
//
//  Visible-for-test: the supabase client is injectable so unit tests
//  can drive presence events deterministically.
//
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase as defaultSupabase } from '@/integrations/supabase/client';
import type { PeerCursor } from '@/lib/minimapCursors';

export interface SelfCursor {
  userId: string;
  userName: string;
  color: string;
  /** Viewport center in canvas coords. */
  x: number;
  y: number;
}

export interface UsePeerCursorsOptions {
  /** Stable id used to build the channel name. */
  workflowId: string | null;
  /** The local user's current cursor. Pass null to disable broadcasting. */
  self: SelfCursor | null;
  /** Throttle for own-cursor track() calls. Default 200ms. */
  throttleMs?: number;
  /**
   * Inject a custom supabase-like client for tests. Must expose
   * `channel(name)` returning an object with .on / .subscribe / .track
   * / .untrack and `removeChannel(channel)`.
   */
  client?: SupabaseLike;
}

interface SupabaseLike {
  channel: (name: string, opts?: unknown) => RealtimeChannel;
  removeChannel: (channel: RealtimeChannel) => Promise<'ok' | 'timed out' | 'error'>;
}

/**
 * Returns the current set of peer cursors. Re-rendered on every presence
 * sync; consumers should pass the result straight into <CursorsLayer>.
 */
export function usePeerCursors({
  workflowId,
  self,
  throttleMs = 200,
  client,
}: UsePeerCursorsOptions): PeerCursor[] {
  const supabase = (client ?? (defaultSupabase as unknown)) as SupabaseLike;
  const [peers, setPeers] = useState<PeerCursor[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastTrackRef = useRef<number>(0);
  const selfRef = useRef<SelfCursor | null>(self);
  selfRef.current = self;

  // Resync peers from the channel's presence state.
  const syncFromState = useCallback(() => {
    const channel = channelRef.current;
    const localUser = selfRef.current?.userId;
    if (!channel) return;
    const state = (channel as unknown as {
      presenceState: () => Record<string, Array<Partial<PeerCursor> & { userId: string }>>;
    }).presenceState();
    const next: PeerCursor[] = [];
    for (const [, entries] of Object.entries(state)) {
      const entry = entries[entries.length - 1];
      if (!entry || typeof entry.userId !== 'string') continue;
      if (localUser && entry.userId === localUser) continue;
      if (
        typeof entry.userName !== 'string' ||
        typeof entry.color !== 'string' ||
        typeof entry.x !== 'number' ||
        typeof entry.y !== 'number'
      ) {
        continue;
      }
      next.push({
        userId: entry.userId,
        userName: entry.userName,
        color: entry.color,
        x: entry.x,
        y: entry.y,
        lastSeenAt:
          typeof entry.lastSeenAt === 'number' ? entry.lastSeenAt : Date.now(),
      });
    }
    setPeers(next);
  }, []);

  // Channel lifecycle: subscribe/unsubscribe on workflowId change.
  useEffect(() => {
    if (!workflowId) {
      setPeers([]);
      return;
    }
    const channel = supabase.channel(`cursors:${workflowId}`, {
      config: { presence: { key: selfRef.current?.userId ?? 'anon' } },
    });
    channelRef.current = channel;
    channel
      .on('presence', { event: 'sync' }, syncFromState)
      .on('presence', { event: 'join' }, syncFromState)
      .on('presence', { event: 'leave' }, syncFromState)
      .subscribe(async (status: string) => {
        if (status === 'SUBSCRIBED' && selfRef.current) {
          await (channel as unknown as { track: (p: unknown) => Promise<unknown> }).track({
            ...selfRef.current,
            lastSeenAt: Date.now(),
          });
        }
      });
    return () => {
      channelRef.current = null;
      void supabase.removeChannel(channel);
    };
  }, [workflowId, supabase, syncFromState]);

  // Throttled track() on cursor changes.
  useEffect(() => {
    const channel = channelRef.current;
    if (!channel || !self) return;
    const now = Date.now();
    if (now - lastTrackRef.current < throttleMs) return;
    lastTrackRef.current = now;
    void (channel as unknown as { track: (p: unknown) => Promise<unknown> }).track({
      ...self,
      lastSeenAt: now,
    });
  }, [self, throttleMs]);

  return peers;
}
